import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import DOMPurify from "dompurify";

// Wrap points for the icon titles. Width alone can't produce these: "Data Centre"
// drops its "&" to line two, and the widths in play sit within a few px of each
// other. Keys are matched on collapsed whitespace because the CMS text carries
// stray double spaces. A title carrying its own <br> wins, so these entries can be
// retired once the CMS text supplies the breaks.
const TITLE_LINE_BREAKS = {
  "Network Connectivity Services": ["Network", "Connectivity", "Services"],
  "Data Centre & Colocation Services": ["Data Centre", "& Colocation", "Services"],
  "Sovereign AI & Cloud Services": ["Sovereign AI", "& Cloud", "Services"],
};

const SOVEREIGN_AI_PAGE_URL = "/sovereign-ai-and-cloud-services";

const titleHtml = (title) => {
  if (!title) return "";
  if (/<br\s*\/?>/i.test(title)) return title;
  const key = title.trim().replace(/\s+/g, " ");
  return (TITLE_LINE_BREAKS[key] || [key]).join("<br>");
};

const isSovereignAiService = (title = "") =>
  title.trim().replace(/\s+/g, " ").toLowerCase().includes("sovereign ai");

const Services = ({ homeData }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(null);
  const [servicesData, setServicesData] = useState([]);
  const [servicesIconData, setServicesIconData] = useState([]);
  const [generalServicesData, setGenaralServicesData] = useState({});

  const homeServiceRef = useRef(null);

  const handleTabClick = (serviceIcon) => {
    if (isSovereignAiService(serviceIcon?.icon_title)) {
      window.scrollTo(0, 0);
      navigate(SOVEREIGN_AI_PAGE_URL);
      return;
    }

    setActiveTab(serviceIcon.id);
    homeServiceRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleClick = () => {
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    if (
      homeData?.data?.data?.home?.home_services_system_data &&
      homeData?.data?.data?.home?.home_services_icon_data
    ) {
      const system_service_data =
        homeData.data.data.home.home_services_system_data;
      const system_service_icon_data =
        homeData.data.data.home.home_services_icon_data;
      const general_setting_data =
        homeData.data.data.general_settings.general_settings_data;

      setServicesData(system_service_data);
      setServicesIconData(system_service_icon_data);

      const serviceDataInfo = general_setting_data.find(
        (item) => item.tag === "home_services",
      );

      if (serviceDataInfo) {
        const parsedData = JSON.parse(serviceDataInfo.response);
        setGenaralServicesData(parsedData);

        if (system_service_data.length > 0) {
          setActiveTab(system_service_data[0].home_services_icon_id);
        }
      }
    }
  }, [homeData]);

  return (
    <>
      {generalServicesData.title && (
        <div className="service">
          <div className="container">
            <div className="heading">
              <h2>
                {generalServicesData.title}{" "}
                <span>
                  {generalServicesData.sub_title}
                  <span className="fontchange">
                    {generalServicesData.italic_title}
                  </span>
                </span>
              </h2>
              <span className="sub-description">
                {generalServicesData.description}
              </span>
            </div>

            {/* SERVICE ICONS */}
            <ul className="tabs">
              {servicesIconData.map((serviceIcon) => (
                <li key={serviceIcon.id}>
                  <button
                    className={
                      activeTab === serviceIcon.id
                        ? "tab-btn active"
                        : "tab-btn"
                    }
                    onClick={() => handleTabClick(serviceIcon)}
                  >
                    <figure className="icon">
                      <img
                        src={`${serviceIcon.icon_image}?w=100`}
                        srcSet={`
                          ${serviceIcon.icon_image}?w=100 100w,
                          ${serviceIcon.icon_image}?w=200 200w
                        `}
                        sizes="100px"
                        width={100}
                        height={100}
                        loading="lazy"
                        alt={serviceIcon.altText}
                      />
                    </figure>
                    <span
                      className="title"
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(titleHtml(serviceIcon.icon_title), {
                          ALLOWED_TAGS: ["br"],
                        }),
                      }}
                    />
                    <span className="arrow"></span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* SERVICE CONTENT */}
      <div
        className="tab-content section-space home-service"
        ref={homeServiceRef}
      >
        <div className="container">
          {servicesData.map((service) => (
            <div key={service.id}>
              {activeTab === service.home_services_icon_id && (
                <div className="sec-row">
                  <div className="sec-col lft-col">
                    <div
                      dangerouslySetInnerHTML={{
                        __html: service.service_description,
                      }}
                    />
                    {service.button_url && (
                      <Link
                        className="button"
                        to={service.button_url}
                        onClick={handleClick}
                      >
                        Know more
                      </Link>
                    )}
                  </div>

                  {/* MAIN SERVICE IMAGE */}
                  <div className="sec-col rgt-col">
                    <img
                      src={`${service.service_image}?w=600`}
                      srcSet={`
                        ${service.service_image}?w=400 400w,
                        ${service.service_image}?w=600 600w,
                        ${service.service_image}?w=900 900w
                      `}
                      sizes="(max-width: 768px) 100vw, 551px"
                      width={551}
                      height={351}
                      loading="lazy"
                      alt={service.altText}
                    />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default Services;
